import {
  getFirestore,
  collection,
  doc,
  addDoc,
  updateDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  Timestamp,
  serverTimestamp,
} from 'firebase/firestore';
import { firebaseApp } from '../firebase.js';

const db = getFirestore(firebaseApp);

/**
 * Create a new support ticket
 */
export const createTicket = async (
  userId,
  userEmail,
  userName,
  userType,
  ticketData
) => {
  try {
    // Generate a human-friendly ticket code so users can track their requests
    const ticketCode = `TKT-${Date.now().toString(36).toUpperCase()}-${Math.floor(Math.random() * 1000)
      .toString()
      .padStart(3, '0')}`;

    // Get current auth user to verify
    const { getAuth } = await import('firebase/auth');
    const auth = getAuth();
    const currentUser = auth.currentUser;

    console.log('Firebase Auth current user:', {
      uid: currentUser?.uid,
      email: currentUser?.email,
      isAuthenticated: !!currentUser,
    });

    console.log('Attempting to create ticket with data:', {
      userId,
      userEmail,
      userName,
      userType,
      type: ticketData.type,
      subject: ticketData.subject,
      priority: ticketData.priority || 'medium',
      ticketCode,
    });

    const ticketRef = await addDoc(collection(db, 'supportTickets'), {
      userId,
      userEmail: userEmail.toLowerCase(), // Normalize email for case-insensitive matching
      userName,
      userType,
      type: ticketData.type,
      subject: ticketData.subject,
      description: ticketData.description,
      priority: ticketData.priority || 'medium',
      status: 'open',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      ticketCode,
      //attachments: ticketData.attachments || [],
      //messages: [],
    });

    return {
      ticketDocId: ticketRef.id,
      ticketCode,
    };
  } catch (error) {
    console.error('Error creating ticket:', error);
    console.error('Error details:', {
      code: error?.code,
      message: error?.message,
      name: error?.name,
    });
    throw error;
  }
};

/**
 * Get all tickets for a specific user
 */
export const getUserTickets = async (userId) => {
  try {
    // Primary query: ordered by createdAt for newest-first display
    const orderedQuery = query(
      collection(db, 'supportTickets'),
      where('userId', '==', userId),
      orderBy('createdAt', 'desc')
    );

    const snapshot = await getDocs(orderedQuery);
    return snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
  } catch (error) {
    console.error('Error fetching user tickets:', error);

    // Fallback: if composite index is missing or orderBy is disallowed, retry without ordering
    try {
      const unorderedQuery = query(
        collection(db, 'supportTickets'),
        where('userId', '==', userId)
      );

      const fallbackSnapshot = await getDocs(unorderedQuery);
      const tickets = fallbackSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      // Sort client-side by createdAt (newest first) if available
      return tickets.sort((a, b) => {
        const aDate = a.createdAt?.toMillis ? a.createdAt.toMillis() : 0;
        const bDate = b.createdAt?.toMillis ? b.createdAt.toMillis() : 0;
        return bDate - aDate;
      });
    } catch (fallbackError) {
      console.error('Fallback ticket fetch also failed:', fallbackError);
      // Last resort: return empty list so UI remains functional
      return [];
    }
  }
};

/**
 * Get a single ticket by ID
 */
export const getTicketById = async (ticketId) => {
  try {
    const ticketDoc = await getDoc(doc(db, 'supportTickets', ticketId));

    if (!ticketDoc.exists()) {
      return null;
    }

    return {
      id: ticketDoc.id,
      ...ticketDoc.data(),
    };
  } catch (error) {
    console.error('Error fetching ticket:', error);
    throw new Error('Failed to fetch ticket');
  }
};

/**
 * Get a ticket by ticket code and email (for public tracking)
 */
export const getTicketByCodeAndEmail = async (ticketCode, email) => {
  console.log('getTicketByCodeAndEmail called with:', { ticketCode, email });
  
  // Normalize inputs for case-insensitive comparison
  const normalizedTicketCode = ticketCode.trim().toUpperCase();
  const normalizedEmail = email.trim().toLowerCase();
  
  console.log('Normalized inputs:', { normalizedTicketCode, normalizedEmail });
  
  try {
    // Try with composite query first
    const q = query(
      collection(db, 'supportTickets'),
      where('ticketCode', '==', normalizedTicketCode),
      where('userEmail', '==', normalizedEmail)
    );

    console.log('Attempting composite query...');
    const snapshot = await getDocs(q);
    console.log('Composite query result:', { empty: snapshot.empty, size: snapshot.size });
    
    if (snapshot.empty) {
      console.log('No tickets found with composite query');
      return null;
    }

    // Return the first matching ticket
    const ticketDoc = snapshot.docs[0];
    const result = {
      id: ticketDoc.id,
      ...ticketDoc.data(),
    };
    console.log('Ticket found via composite query:', result);
    return result;
  } catch (error) {
    console.error('Error with composite query:', error);
    console.error('Error code:', error?.code);
    console.error('Error message:', error?.message);
    
    // Fallback 1: Query by ticketCode only and filter client-side
    try {
      console.log('Trying fallback query with single where clause (ticketCode)...');
      const fallbackQuery = query(
        collection(db, 'supportTickets'),
        where('ticketCode', '==', normalizedTicketCode)
      );

      const fallbackSnapshot = await getDocs(fallbackQuery);
      console.log('Fallback query result:', { empty: fallbackSnapshot.empty, size: fallbackSnapshot.size });
      
      if (fallbackSnapshot.empty) {
        console.log('No tickets found with fallback query');
        return null;
      }

      // Filter by email client-side (case-insensitive)
      const matchingTicket = fallbackSnapshot.docs.find(
        (doc) => doc.data().userEmail?.toLowerCase() === normalizedEmail
      );

      if (!matchingTicket) {
        console.log('Ticket found but email does not match');
        return null;
      }

      const result = {
        id: matchingTicket.id,
        ...matchingTicket.data(),
      };
      console.log('Ticket found via fallback query:', result);
      return result;
    } catch (fallbackError) {
      console.error('Fallback query 1 also failed:', fallbackError);
      console.error('Fallback error code:', fallbackError?.code);
      
      // Fallback 2: Get all tickets and filter client-side (last resort)
      try {
        console.log('Trying final fallback - fetch all tickets...');
        const allTicketsSnapshot = await getDocs(collection(db, 'supportTickets'));
        console.log('All tickets fetched:', allTicketsSnapshot.size);
        
        const matchingTicket = allTicketsSnapshot.docs.find(
          (doc) => {
            const data = doc.data();
            const docTicketCode = data.ticketCode?.toUpperCase();
            const docEmail = data.userEmail?.toLowerCase();
            console.log('Checking ticket:', { docTicketCode, docEmail, normalizedTicketCode, normalizedEmail });
            return docTicketCode === normalizedTicketCode && docEmail === normalizedEmail;
          }
        );

        if (!matchingTicket) {
          console.log('No matching ticket found in all tickets');
          return null;
        }

        const result = {
          id: matchingTicket.id,
          ...matchingTicket.data(),
        };
        console.log('Ticket found via final fallback:', result);
        return result;
      } catch (finalError) {
        console.error('Final fallback also failed:', finalError);
        console.error('Final error code:', finalError?.code);
        console.error('Final error message:', finalError?.message);
        // Return null instead of throwing to prevent UI errors
        return null;
      }
    }
  }
};

/**
 * Update ticket status or other fields (Admin function)
 */
export const updateTicket = async (
  ticketId,
  updates
) => {
  try {
    const ticketRef = doc(db, 'supportTickets', ticketId);

    const updateData = {
      ...updates,
      updatedAt: serverTimestamp(),
    };

    if (updates.status === 'resolved' || updates.status === 'closed') {
      updateData.resolvedAt = serverTimestamp();
    }

    await updateDoc(ticketRef, updateData);
  } catch (error) {
    console.error('Error updating ticket:', error);
    throw new Error('Failed to update ticket');
  }
};

/**
 * Add a message to a ticket
 */
export const addTicketMessage = async (
  ticketId,
  senderId,
  senderName,
  senderType,
  message,
  attachments
) => {
  try {
    const ticketRef = doc(db, 'supportTickets', ticketId);
    const ticketDoc = await getDoc(ticketRef);

    if (!ticketDoc.exists()) {
      throw new Error('Ticket not found');
    }

    const ticketData = ticketDoc.data();
    const messages = ticketData.messages || [];

    const newMessage = {
      id: `msg_${Date.now()}`,
      ticketId,
      senderId,
      senderName,
      senderType,
      message,
      createdAt: Timestamp.now(),
      attachments: attachments || [],
    };

    messages.push(newMessage);

    await updateDoc(ticketRef, {
      messages,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error adding ticket message:', error);
    throw new Error('Failed to add message to ticket');
  }
};

/**
 * Get all tickets (Admin function)
 */
export const getAllTickets = async (
  filterStatus
) => {
  try {
    let q;

    if (filterStatus) {
      q = query(
        collection(db, 'supportTickets'),
        where('status', '==', filterStatus),
        orderBy('createdAt', 'desc')
      );
    } else {
      q = query(
        collection(db, 'supportTickets'),
        orderBy('createdAt', 'desc')
      );
    }

    const snapshot = await getDocs(q);
    return snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
  } catch (error) {
    console.error('Error fetching all tickets:', error);
    throw new Error('Failed to fetch tickets');
  }
};

/**
 * Get ticket statistics
 */
export const getTicketStats = async (userId) => {
  try {
    let q;

    if (userId) {
      q = query(collection(db, 'supportTickets'), where('userId', '==', userId));
    } else {
      q = query(collection(db, 'supportTickets'));
    }

    const snapshot = await getDocs(q);
    const tickets = snapshot.docs.map((doc) => doc.data());

    const stats = {
      total: tickets.length,
      open: tickets.filter((t) => t.status === 'open').length,
      inProgress: tickets.filter((t) => t.status === 'in_progress').length,
      resolved: tickets.filter((t) => t.status === 'resolved').length,
      closed: tickets.filter((t) => t.status === 'closed').length,
    };

    return stats;
  } catch (error) {
    console.error('Error fetching ticket stats:', error);
    throw new Error('Failed to fetch ticket statistics');
  }
};

